// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import * as api from '../api/client'
import { useAuth } from './AuthContext'

/**
 * Per-pharmacy module entitlement state for the current user (ADR-019 D-9).
 *
 * `known` is false until the first fetch resolves. Consumers should treat
 * "unknown" as visible (optimistic) so entitled users never see a flicker;
 * only hide a module once we know it's disabled. The backend enforces the
 * real gate regardless, so optimistic visibility is safe.
 */
interface ModulesState {
  modules: Record<string, boolean>
  known: boolean
  /** Hide only when we KNOW the module is disabled. Unknown => not disabled (show). */
  isDisabled: (key: string) => boolean
}

const Ctx = createContext<ModulesState | null>(null)

export function ModulesProvider({ children }: { children: ReactNode }) {
  const { me } = useAuth()
  const [modules, setModules] = useState<Record<string, boolean>>({})
  const [known, setKnown] = useState(false)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (!me?.authenticated) { setModules({}); setKnown(false); setFailed(false); return }
    api.getMyModules()
      .then(r => { if (!cancelled) { setModules(r.modules ?? {}); setFailed(false); setKnown(true) } })
      // Fail OPEN: if the endpoint is unreachable (e.g. backend not yet deployed) we must NOT
      // hide modules — the backend is the real gate. Treat as "can't determine" => show all.
      .catch(() => { if (!cancelled) { setFailed(true); setKnown(true) } })
    return () => { cancelled = true }
  }, [me?.authenticated])

  // Hide only when we positively know a module is disabled. A fetch failure never hides.
  const isDisabled = (key: string) => known && !failed && modules[key] !== true

  return <Ctx.Provider value={{ modules, known, isDisabled }}>{children}</Ctx.Provider>
}

export function useModules(): ModulesState {
  const v = useContext(Ctx)
  // Tolerate use outside the provider (e.g. tests) by treating everything as unknown/visible.
  return v ?? { modules: {}, known: false, isDisabled: () => false }
}
